import { useMemo, useState, type ReactNode } from 'react';
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import { cn } from '../lib/cn.js';

export interface TableColumn<T> {
  id: string;
  header: ReactNode;
  cell: (row: T, rowIndex: number) => ReactNode;
  sortable?: boolean;
  sortValue?: (row: T) => string | number;
  align?: 'left' | 'right' | 'center';
  width?: string;
}

export interface TableProps<T> {
  columns: TableColumn<T>[];
  rows: T[];
  rowKey: (row: T, idx: number) => string;
  loading?: boolean;
  emptyState?: ReactNode;
  caption?: string;
  className?: string;
  onRowClick?: (row: T) => void;
}

type SortState = { id: string; dir: 'asc' | 'desc' } | null;

export function Table<T>({ columns, rows, rowKey, loading, emptyState, caption, className, onRowClick }: TableProps<T>) {
  const [sort, setSort] = useState<SortState>(null);

  const sorted = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.id === sort.id);
    if (!col?.sortValue) return rows;
    const sortValue = col.sortValue;
    return [...rows].sort((a, b) => {
      const av = sortValue(a);
      const bv = sortValue(b);
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sort.dir === 'asc' ? cmp : -cmp;
    });
  }, [rows, sort, columns]);

  const toggleSort = (id: string) => {
    setSort((s) => {
      if (!s || s.id !== id) return { id, dir: 'asc' };
      if (s.dir === 'asc') return { id, dir: 'desc' };
      return null;
    });
  };

  return (
    <div className={cn('overflow-hidden rounded-lg border border-border-subtle bg-bg-surface-1', className)}>
      <table className="w-full border-collapse">
        {caption && <caption className="sr-only">{caption}</caption>}
        <thead>
          <tr className="bg-bg-surface-2 border-b border-border-subtle">
            {columns.map((c) => {
              const sorted = sort?.id === c.id;
              return (
                <th
                  key={c.id}
                  scope="col"
                  style={c.width ? { width: c.width } : undefined}
                  className={cn(
                    'px-4 py-3 text-sm font-semibold text-text-secondary text-left',
                    c.align === 'right' && 'text-right',
                    c.align === 'center' && 'text-center',
                  )}
                  aria-sort={sorted ? (sort?.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                >
                  {c.sortable ? (
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 hover:text-text-primary"
                      onClick={() => toggleSort(c.id)}
                    >
                      {c.header}
                      {!sorted && <ChevronsUpDown className="size-3.5 opacity-50" aria-hidden />}
                      {sorted && sort?.dir === 'asc' && <ArrowUp className="size-3.5" aria-hidden />}
                      {sorted && sort?.dir === 'desc' && <ArrowDown className="size-3.5" aria-hidden />}
                    </button>
                  ) : (
                    c.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {loading &&
            Array.from({ length: 4 }).map((_, i) => (
              <tr key={`skeleton-${i}`} className="border-b border-border-subtle last:border-0">
                {columns.map((c) => (
                  <td key={c.id} className="px-4 py-4 h-[60px]">
                    <span className="block h-3 w-2/3 rounded bg-bg-surface-3 animate-pulse" />
                  </td>
                ))}
              </tr>
            ))}
          {!loading && sorted.length === 0 && (
            <tr>
              <td colSpan={columns.length} className="p-0">
                {emptyState ?? (
                  <div className="px-6 py-10 text-center text-text-secondary">No rows.</div>
                )}
              </td>
            </tr>
          )}
          {!loading &&
            sorted.map((row, idx) => {
              const interactive = !!onRowClick;
              return (
                <tr
                  key={rowKey(row, idx)}
                  onClick={interactive ? () => onRowClick(row) : undefined}
                  className={cn(
                    'border-b border-border-subtle last:border-0 transition-colors',
                    interactive && 'cursor-pointer hover:bg-bg-surface-2',
                  )}
                >
                  {columns.map((c) => (
                    <td
                      key={c.id}
                      className={cn(
                        'px-4 py-4 text-base text-text-primary align-middle min-h-[60px]',
                        c.align === 'right' && 'text-right',
                        c.align === 'center' && 'text-center',
                      )}
                    >
                      {c.cell(row, idx)}
                    </td>
                  ))}
                </tr>
              );
            })}
        </tbody>
      </table>
    </div>
  );
}
